// Cajón lateral genérico: velo, panel que entra desde la derecha y salida con
// animación. Es el patrón que estaba escrito a mano en `CargaDocumentosDrawer`
// y `ChatCargaDrawer`; aquí vive UNA vez.
//
// Comportamiento que no es obvio:
//  - **Entrada**: el panel se monta fuera de pantalla y se desliza en el frame
//    siguiente (`requestAnimationFrame`). Montarlo ya dentro no animaría.
//  - **Salida**: `onClose` se llama TRAS la animación (`DURACION_MS`), no al
//    pulsar. El padre desmonta cuando recibe `onClose`, así que llamarlo antes
//    cortaría la animación de salida en seco.
//  - **Escape sólo cierra el de ENCIMA.** Una ficha puede abrir otro cajón
//    (documentos) o un diálogo encima de sí misma, y con un listener por capa un
//    solo Escape cerraría todas. Se apilan en `PILA` (`useCapaEscape`).
//  - **Foco**: al abrir pasa al panel (para que Escape y Tab actúen dentro) y al
//    cerrar vuelve a quien lo tenía, que es lo que hace un diálogo accesible.
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

/** Lo que dura la animación; coincide con el `transition` del panel. */
const DURACION_MS = 340

/** Capas abiertas que responden a Escape, la última es la de encima. Módulo y
 *  no estado: no pinta nada y tiene que verse entre componentes distintos. */
const PILA: symbol[] = []

/**
 * Registra una capa (cajón, diálogo) en la pila de Escape: mientras `activa`,
 * Escape llama a `alEscape` SÓLO si es la de encima. Así un diálogo abierto
 * dentro de la ficha se cierra solo, sin arrastrar a la ficha con él.
 */
export function useCapaEscape(activa: boolean, alEscape: () => void) {
  const id = useRef(Symbol('capa'))
  const cb = useRef(alEscape)
  cb.current = alEscape
  useEffect(() => {
    if (!activa) return
    const miId = id.current
    PILA.push(miId)
    const alPulsar = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && PILA[PILA.length - 1] === miId) cb.current()
    }
    document.addEventListener('keydown', alPulsar)
    return () => {
      document.removeEventListener('keydown', alPulsar)
      const i = PILA.indexOf(miId)
      if (i >= 0) PILA.splice(i, 1)
    }
  }, [activa])
}

interface Props {
  /** `false` = no se monta nada. El padre lo pone a `false` al recibir `onClose`. */
  open: boolean
  onClose: () => void
  dark: boolean
  /** Clase de ancho de Tailwind del panel (p. ej. `w-[560px]`). `max-w-full` se añade. */
  width?: string
  /** Para lectores de pantalla. */
  ariaLabel: string
  /** El contenido, o una función que recibe `cerrar` para que sus botones de
   *  cierre (la X de la cabecera) salgan CON animación en vez de cortar en seco. */
  children: ReactNode | ((cerrar: () => void) => ReactNode)
  /** Dar el foco al panel al abrir. Falso si el contenido enfoca algo propio
   *  (el chat enfoca su campo de texto). */
  enfocar?: boolean
}

export function Drawer({ open, onClose, dark, width = 'w-[560px]', ariaLabel, children, enfocar = true }: Props) {
  const [visible, setVisible] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  const cerrar = useCallback(() => {
    setVisible(false)
    setTimeout(() => onCloseRef.current(), DURACION_MS)
  }, [])

  useCapaEscape(open, cerrar)

  // Entrada y foco. Sólo cuando `open` cambia.
  useEffect(() => {
    if (!open) return
    const previo = document.activeElement as HTMLElement | null
    const raf = requestAnimationFrame(() => {
      setVisible(true)
      if (enfocar) panelRef.current?.focus()
    })
    return () => {
      cancelAnimationFrame(raf)
      setVisible(false)
      previo?.focus?.()
    }
  }, [open, enfocar])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end">
      <div
        onClick={cerrar}
        className="absolute inset-0 cursor-pointer"
        style={{
          backgroundColor: visible ? 'rgba(0,0,0,0.52)' : 'rgba(0,0,0,0)',
          backdropFilter: visible ? 'blur(3px)' : 'blur(0px)',
          transition: `background-color ${DURACION_MS}ms ease, backdrop-filter ${DURACION_MS}ms ease`,
        }}
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        className={`relative flex flex-col h-full max-w-full outline-none border-l ${width} ${
          dark ? 'bg-[#0d1117] border-white/[0.07]' : 'bg-white border-slate-200'
        }`}
        style={{
          transform: visible ? 'translateX(0)' : 'translateX(100%)',
          transition: `transform ${DURACION_MS}ms cubic-bezier(0.32, 0, 0.1, 1)`,
          boxShadow: dark
            ? '-32px 0 80px rgba(0,0,0,0.6), -1px 0 0 rgba(255,255,255,0.05)'
            : '-32px 0 80px rgba(0,0,0,0.12), -1px 0 0 rgba(0,0,0,0.06)',
        }}
      >
        {typeof children === 'function' ? children(cerrar) : children}
      </div>
    </div>
  )
}
